/**
 * PURPOSE: Consultar la estructura vigente de Fases y editar únicamente el número de grupos.
 * RESPONSIBILITIES: Mostrar contadores sincronizados y guardar nro_grupos mediante un endpoint parcial seguro.
 * COLLABORATORS: FasesArea.jsx, PhaseActionsBar.jsx y phaseToolsService.js.
 * ROUTE: winscore/src/area/FasesArea/components/PhaseSummaryModal.jsx
 */
import React, { useEffect, useState } from "react";
import Modal from "@mui/joy/Modal";
import ModalClose from "@mui/joy/ModalClose";
import Sheet from "@mui/joy/Sheet";
import { FiCalendar, FiFlag, FiGrid, FiUsers } from "react-icons/fi";
import { toast } from "react-hot-toast";
import { updatePhaseGroups } from "../../../services/phaseToolsService.js";

const SummaryValue = ({ icon: Icon, label, value, description }) => (
  <article className="flex min-w-0 items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-lg text-blue-700 shadow-sm ring-1 ring-slate-200">
      <Icon />
    </span>
    <span className="min-w-0">
      <span className="block text-xs font-black uppercase tracking-wide text-slate-500">
        {label}
      </span>
      <span className="mt-1 block text-2xl font-black text-slate-900">
        {Number(value || 0)}
      </span>
      <span className="mt-1 block text-xs leading-5 text-slate-500">
        {description}
      </span>
    </span>
  </article>
);

const PhaseSummaryModal = ({
  open,
  onClose,
  idevent,
  idsport,
  context,
  onChanged,
}) => {
  const config = context?.config || {};
  const closed = Boolean(config.isClosed);
  const [groups, setGroups] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setGroups(Number(config.nro_grupos || 0));
  }, [config.nro_grupos, open]);

  const handleSave = async () => {
    const normalizedGroups = Number(groups);
    if (
      !Number.isInteger(normalizedGroups) ||
      normalizedGroups < 0 ||
      normalizedGroups > 10
    ) {
      toast.error("El número de grupos debe estar entre 0 y 10.");
      return;
    }

    try {
      setSaving(true);
      await updatePhaseGroups({
        idevent,
        idsport,
        nro_grupos: normalizedGroups,
      });
      await onChanged?.();
      toast.success("Número de grupos actualizado.");
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          "No se pudo actualizar el número de grupos."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={saving ? undefined : onClose} sx={{ p: 1.5 }}>
      <Sheet className="mx-auto max-h-[92vh] w-[min(96vw,720px)] overflow-y-auto rounded-2xl border border-slate-200 bg-white shadow-2xl">
        <header className="relative border-b border-slate-200 px-5 py-4 pr-14">
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-600">
            Fases
          </p>
          <h2 className="mt-1 text-xl font-black text-slate-900">
            Resumen de fases
          </h2>
          <p className="mt-1 text-xs leading-5 text-slate-500">
            La cantidad de participantes, fechas y etapas se sincroniza al guardar cambios.
          </p>
          <ModalClose disabled={saving} />
        </header>

        <div className="grid gap-4 p-5 sm:grid-cols-2">
          <label className="rounded-xl border border-blue-200 bg-blue-50/50 p-4">
            <span className="flex items-center gap-2 text-xs font-black uppercase tracking-wide text-blue-700">
              <FiGrid /> Número de grupos
            </span>
            <input
              type="number"
              min="0"
              max="10"
              step="1"
              value={groups}
              onChange={(event) => setGroups(event.target.value)}
              disabled={closed || saving}
              className="mt-3 min-h-[44px] w-full rounded-xl border border-slate-300 bg-white px-3 text-lg font-black text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500"
            />
            <span className="mt-2 block text-xs leading-5 text-slate-500">
              Rango permitido: 0 a 10.
            </span>
          </label>

          <SummaryValue
            icon={FiCalendar}
            label="Fechas primera fase"
            value={config.nro_fechas_grupo}
            description=""
          />
          <SummaryValue
            icon={FiFlag}
            label="Etapas finales"
            value={config.nro_etapas_final}
            description=""
          />
          <SummaryValue
            icon={FiUsers}
            label="Participantes"
            value={config.nro_instituciones_participantes}
            description=""
          />
        </div>

        {closed ? (
          <div className="mx-5 mb-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-bold text-amber-800">
            La configuración está cerrada. Puedes consultar el resumen, pero no editar el número de grupos.
          </div>
        ) : null}

        <footer className="flex justify-end gap-3 border-t border-slate-200 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-black text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Cerrar
          </button>
          {!closed ? (
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-black text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? "Guardando..." : "Guardar grupos"}
            </button>
          ) : null}
        </footer>
      </Sheet>
    </Modal>
  );
};

export default PhaseSummaryModal;
